import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  CopyToClipboardButton,
  Spacer,
  TabsBarLink,
  useClipboardAvailable,
} from '@porsche-code/kado-ui';
import {
  PHeading,
  PTabsBar,
  PText,
} from '@porsche-design-system/components-react';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useMatchRoute,
} from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';

import { COMMON_NAMESPACE, NAMESPACES } from '@/config/i18n';
import { useAbilityContext, usePersonContext } from '@/context';

export const Route = createFileRoute('/profile')({
  component: () => {
    const matchRoute = useMatchRoute();

    if (matchRoute({ to: '/profile' })) {
      return <Navigate to="/profile/general-settings" replace />;
    }

    return <Profile />;
  },
  beforeLoad: () => {
    return {
      breadcrumb: () => (
        <Trans i18nKey="profile.headline" ns={COMMON_NAMESPACE} />
      ),
    };
  },
});

function Profile() {
  const { t } = useTranslation(NAMESPACES);
  const matchRoute = useMatchRoute();
  const { state: personState } = usePersonContext();
  const { state: ability } = useAbilityContext();

  const tabs = [
    {
      routeId: '/profile/general-settings',
      label: t('common:profile.generalSettings'),
    },
    ...(ability.can('manage', 'DashboardEmailNotification')
      ? [
          {
            routeId: '/profile/email-notifications' as const,
            label: t('common:profile.notifications'),
          },
        ]
      : []),
  ];

  const activeTabIndex = tabs.findIndex((tab) =>
    matchRoute({ to: tab.routeId, fuzzy: true }),
  );

  return (
    <>
      <PHeading size="lg" aria-level={1} tag="h1">
        {t('common:profile.headline')}
      </PHeading>
      <Spacer mb="md" />
      <CopyableField
        label={t('common:ppnuid')}
        value={personState.ppnuid}
        successLabel={t('common:profile.copyToClipboard', {
          field: t('common:ppnuid'),
        })}
      />
      <CopyableField
        label={t('common:organization')}
        value={personState.organization?.name}
        successLabel={t('common:profile.copyToClipboard', {
          field: t('common:organization'),
        })}
      />
      <Spacer mb="md" />
      <PTabsBar size="medium" compact activeTabIndex={activeTabIndex}>
        {tabs.map((tab) => (
          <TabsBarLink key={`${tab.routeId}`} to={tab.routeId}>
            {tab.label}
          </TabsBarLink>
        ))}
      </PTabsBar>
      <Spacer mb="md" />
      <Outlet />
    </>
  );
}

const CopyButtonWrapper = styled('span', {
  base: {
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
  },
});

const CopyRow = styled('div', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'sm',
    '&:hover [data-part="copy-button-wrapper"]': {
      visibility: 'visible',
      opacity: 1,
    },
  },
});

const CopyableField = ({
  label,
  value,
  successLabel,
}: {
  label: string;
  value?: string;
  successLabel: string;
}) => {
  const { write: clipboardAvailable } = useClipboardAvailable();

  return (
    <div>
      <CopyRow>
        <PText>
          {label}: {value}
        </PText>
        {clipboardAvailable && value && (
          <CopyButtonWrapper data-part="copy-button-wrapper">
            <CopyToClipboardButton
              hideLabel
              label={label}
              successLabel={successLabel}
              onCopy={() => value}
            />
          </CopyButtonWrapper>
        )}
      </CopyRow>
    </div>
  );
};
